// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint, Icon, TooltipHost } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { primaryTheme } from "components/shell/presentation/AppStyles";
import { ReactNode, useRef, useState } from "react";
import styled from "styled-components";

// Types
export interface NavItem {
    key: string;
    name: string;
    iconName: string;
    onClick: (ev: any) => void;
    automationId?: string;
    disabled?: boolean;
    tooltip?: string;
    badge?: number;
    onRender?: () => JSX.Element | null;
}

export interface NavGroup {
    name?: string;
    items: NavItem[];
}

export interface NavThemeColors {
    primary: string;
    primaryDark: string;
    headerBackground: string;
    headerText: string;
}

interface NavBarProps {
    groups: NavGroup[];
    selectedKey?: string;
    header?: {
        icon: ReactNode;
        title: string;
        onClick?: () => void;
    };
    footer?: ReactNode;
    onExpandedChange?: (expanded: boolean) => void;
    initialExpanded?: boolean;
    themeColors?: NavThemeColors;
}

// Default theme colors (fallback to primaryTheme values)
const defaultThemeColors: NavThemeColors = {
    primary: primaryTheme.palette.themePrimary,
    primaryDark: primaryTheme.palette.themeDark ?? primaryTheme.palette.themePrimary,
    headerBackground: primaryTheme.palette.themePrimary,
    headerText: primaryTheme.palette.white
};

// Styled Components
const NavContainer = styled.div<{ $isExpanded: boolean; }>`
    display: flex;
    flex-direction: column;
    width: ${props => props.$isExpanded ? '210px' : '50px'};
    height: 100%;
    background: #F2F3F3;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: static;
    box-shadow: none;
`;

const NavHeader = styled.div<{ $clickable: boolean; $isExpanded: boolean; $bgColor: string; $bgHoverColor: string; $textColor: string }>`
    display: flex;
    align-items: center;
    padding: 0px 12px;
    background: ${props => props.$bgColor};
    color: ${props => props.$textColor};
    min-height: 45px;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    gap: 17px;
    transition: background 0.2s;
    overflow: hidden;

    &:hover {
        background: ${props => props.$clickable ? props.$bgHoverColor : props.$bgColor};
    }
`;

const NavHeaderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const NavHeaderTitle = styled.div<{ $visible: boolean }>`
    font-family: ${dgFontFamily};
    font-size: 18px;
    font-weight: 600;
    white-space: nowrap;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? 'visible' : 'hidden'};
    transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
`;

const NavGroupContainer = styled.div<{ $isLast: boolean }>`
    padding: 15px 0;
    border-bottom: ${props => props.$isLast ? 'none' : '1px solid rgba(0,0,0,0.08)'};

    &:first-of-type {
        padding-top: 10px;
    }

    &:last-of-type {
        margin-top: auto;
    }
`;

const NavGroupTitle = styled.div<{ $visible: boolean }>`
    font-family: ${dgFontFamily};
    padding: 0 12px 8px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #605E5C;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? 'visible' : 'hidden'};
    max-height: ${props => props.$visible ? '50px' : '0'};
    overflow: hidden;
    transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out, max-height 0.3s ease-in-out;
`;

const NavItemButton = styled.button<{ $isSelected: boolean; $isExpanded: boolean; $disabled: boolean; $primaryColor: string }>`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: ${props => props.$isSelected ? `${props.$primaryColor}1A` : 'transparent'};
    color: ${props => props.$disabled ? '#A19F9D' : (props.$isSelected ? props.$primaryColor : primaryTheme.palette.neutralPrimary)};
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    text-align: left;
    transition: background 0.2s, color 0.2s;
    gap: 12px;
    position: relative;
    opacity: ${props => props.$disabled ? 0.5 : 1};
    overflow: hidden;

    &:hover:not(:disabled) {
        background: ${props => props.$isSelected ? `${props.$primaryColor}26` : 'rgba(0, 0, 0, 0.05)'};
    }

    &:active:not(:disabled) {
        background: ${props => `${props.$primaryColor}33`};
    }

    ${props => props.$isSelected && `
        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 3px;
            background: ${props.$primaryColor};
        }
    `}
`;

const NavItemIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 25px;
`;

const NavItemText = styled.span<{ $visible: boolean }>`
    font-family: ${dgFontFamily};
    font-size: 14px;
    white-space: nowrap;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? 'visible' : 'hidden'};
    transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
`;

const NavItemBadge = styled.span<{ $visible: boolean }>`
    font-family: ${dgFontFamily};
    background: ${primaryTheme.palette.red};
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 600;
    min-width: 18px;
    text-align: center;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? 'visible' : 'hidden'};
    transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
    flex-shrink: 0;
`;

const ExpandButton = styled.button<{ $isExpanded: boolean; $primaryColor: string }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 12px;
    border: none;
    background: transparent;
    color: #605E5C;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
        color: ${props => props.$primaryColor};
    }
`;

const ExpandIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    flex-shrink: 0;
`;

// Component
export const NavigationBar: React.FC<NavBarProps> = ({
    groups,
    selectedKey,
    header,
    footer,
    onExpandedChange,
    initialExpanded = false,
    themeColors
}) => {
    const [isExpanded, setIsExpanded] = useState(initialExpanded);
    const containerRef = useRef<HTMLDivElement>(null);

    // Use provided theme colors or fall back to defaults
    const colors = themeColors ?? defaultThemeColors;

    const handleExpand = () => {
        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);
        onExpandedChange?.(newExpanded);
    };

    const handleItemClick = (ev: any, item: NavItem) => {
        if (item.disabled) {
            return;
        }
        item.onClick(ev);
    };

    const renderNavContent = () => (
        <>
            {header && (
                <NavHeader
                    $clickable={!!header.onClick}
                    $isExpanded={isExpanded}
                    $bgColor={colors.headerBackground}
                    $bgHoverColor={colors.primaryDark}
                    $textColor={colors.headerText}
                    onClick={handleExpand}
                >
                    <NavHeaderIcon>{header.icon}</NavHeaderIcon>
                    <NavHeaderTitle $visible={isExpanded}>
                        {header.title}
                    </NavHeaderTitle>
                </NavHeader>
            )}

            {groups.map((group, groupIndex) => (
                <NavGroupContainer
                    key={groupIndex}
                    $isLast={groupIndex === groups.length - 1}
                >
                    {group.name && (
                        <NavGroupTitle $visible={isExpanded}>
                            {group.name}
                        </NavGroupTitle>
                    )}
                    {group.items.map(item => {
                        if (!!item.onRender) {
                            return <div key={item.key}>{item.onRender()}</div>;
                        }

                        const itemContent = (
                            <NavItemButton
                                key={item.key}
                                $isSelected={selectedKey === item.key}
                                $isExpanded={isExpanded}
                                $disabled={!!item.disabled}
                                $primaryColor={colors.primary}
                                onClick={(ev) => handleItemClick(ev, item)}
                                data-automation-id={item.automationId}
                                disabled={item.disabled}
                                title={(isExpanded) ? undefined : item.name}
                            >
                                <NavItemIcon>
                                    <Icon
                                        iconName={item.iconName}
                                        styles={{ root: { fontSize: '20px' } }}
                                    />
                                </NavItemIcon>
                                <NavItemText $visible={isExpanded}>
                                    {item.name}
                                </NavItemText>
                                {item.badge !== undefined && item.badge > 0 && (
                                    <NavItemBadge $visible={isExpanded}>
                                        {item.badge > 99 ? '99+' : item.badge}
                                    </NavItemBadge>
                                )}
                            </NavItemButton>
                        );

                        // Show tooltip when collapsed
                        if (!isExpanded) {
                            return (
                                <TooltipHost
                                    key={item.key}
                                    content={`${item.name}${item.tooltip ? ` - ${item.tooltip}` : ''}`}
                                    directionalHint={DirectionalHint.rightCenter}
                                    styles={{
                                        root: { display: 'block' },
                                    }}
                                >
                                    {itemContent}
                                </TooltipHost>
                            );
                        }

                        return itemContent;
                    })}
                </NavGroupContainer>
            ))}

            {footer}

            <ExpandButton
                $isExpanded={isExpanded}
                $primaryColor={colors.primary}
                onClick={handleExpand}
                title={isExpanded ? 'Collapse menu' : 'Expand menu'}
            >
                <ExpandIconWrapper>
                    <Icon
                        iconName={isExpanded ? 'DoubleChevronLeft' : 'DoubleChevronRight'}
                        styles={{ root: { fontSize: '16px' } }}
                    />
                </ExpandIconWrapper>
            </ExpandButton>
        </>
    );

    return (
        <NavContainer
            ref={containerRef}
            $isExpanded={isExpanded}
        >
            {renderNavContent()}
        </NavContainer>
    );
};
